/**
 * CV Notes API service for ATS.
 */

import { createApiClient } from '@aaas/shared';
import type { CVNote, CVNoteListResponse } from '../types';

interface CVNoteApiResponse {
  success: boolean;
  data: CVNote;
  message: string;
}

interface CVNoteListApiResponse {
  success: boolean;
  data: CVNoteListResponse;
  message: string;
}

interface DeleteNoteApiResponse {
  success: boolean;
  data: null;
  message: string;
}

class NoteService {
  /**
   * List notes for a CV.
   */
  async listNotes(cvId: string, workspaceId: string, idToken: string): Promise<CVNoteListResponse> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<CVNoteListApiResponse>(`/cvs/${cvId}/notes`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to list notes');
    }

    return response.data.data;
  }

  /**
   * Create a note on a CV.
   */
  async createNote(
    cvId: string,
    content: string,
    workspaceId: string,
    idToken: string
  ): Promise<CVNote> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<CVNoteApiResponse>(
      `/cvs/${cvId}/notes`,
      { content },
      {
        headers: { 'X-Workspace-ID': workspaceId },
      }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create note');
    }

    return response.data.data;
  }

  /**
   * Delete a note from a CV.
   */
  async deleteNote(
    cvId: string,
    noteId: string,
    workspaceId: string,
    idToken: string
  ): Promise<void> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.delete<DeleteNoteApiResponse>(`/cvs/${cvId}/notes/${noteId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete note');
    }
  }
}

const noteService = new NoteService();
export default noteService;
